import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { 
  Logout,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { useTranslation } from 'react-i18next';
import { Loading } from './Loading';
import { captureException } from '../utils/captureException';
import theme from '../theme';

interface LayoutProps extends WithDeviceProps {
  children: React.ReactNode;
  drawerOpen: boolean;
  setDrawerOpen: (value: boolean) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, isMobileDevice, drawerOpen, setDrawerOpen }) => {
  const { logout, loading, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Show loading screen when user is not yet validated or identified
  if (loading) {
    return (
      // <Box
      //   sx={{
      //     display: 'flex',
      //     justifyContent: 'center',
      //     alignItems: 'center',
      //     height: '100vh',
      //     bgcolor: 'background.default',
      //   }}
      // >
      //   <CircularProgress />
      // </Box>
      <Loading />
    );
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      captureException(err, { tags: { feature: 'layout' }, extra: { action: 'handleLogout' } });
      console.error('Failed to log out:', err);
    }
  };

  // Check if user is from pervasive-tech.com domain
  const isAdminUser = user?.email?.split('@')[1]?.toLowerCase() === 'pervasive-tech.com';

  const mainMenu = [
    { text: t('navigation.home'), icon: <HomeIcon />, path: '/', show: true },
    { text: t('navigation.projects'), icon: <SearchIcon />, path: '/projects', show: true },
  ];

  const secondaryMenu = [
    { text: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings', show: !isMobileDevice },
    { text: 'Lanzamiento', icon: <DescriptionIcon />, path: '/lanzamiento', show: !isMobileDevice },
  ];

  const drawerContent = (
    <Box
      sx={{ width: isMobileDevice ? '100vw' : 250 }}
      role="presentation"
      onClick={handleDrawerClose}
      onKeyDown={handleDrawerClose}
    >
      <Toolbar />
      <Divider />
      <List>
        {mainMenu.map((item) => (
          item.show && (
            <ListItem key={item.text} disablePadding>
              <ListItemButton onClick={() => navigate(item.path)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>
      <Divider />
      
      {!isMobileDevice && (<>
        <List>
        {secondaryMenu.map((item) => (
          item.show && (
            <ListItem key={item.text} disablePadding>
              <ListItemButton onClick={() => navigate(item.path)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>
      <Divider />
      </>
      )}
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><Logout /></ListItemIcon>
            <ListItemText primary={t('common.logout')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      overflow: 'hidden',
      margin: 0,
      padding: 0
    }}>
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isMobileDevice ? '100%' : 250,
          },
        }}
      >
        {drawerContent}
      </Drawer>
      

      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          height: '100vh',
          overflow: 'hidden'
        }}
      >
        {/* Spacer for AppBar */}
        <Box sx={{ height: isMobileDevice || isSmallScreen ? '56px' : '64px', flexShrink: 0 }} />
        
        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 0,
            paddingBottom: '0px' // Add space for footer on mobile
          }}
        >
          {children}
        </Box>
        {/* Spacer for AppBar */}
        <Box sx={{ height: '56px', flexShrink: 0 }} />
      </Box>
    </Box>
  );
};

export default withDevice(Layout); 